class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('blur', this.onBrInputBlur.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.disableNativePopup = this.dataset.disableNativePopup === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    // ── Bloomreach popup keep-open on keyboard dismiss ────────────────────────
    // See onBrInputBlur for why this exists.
    this._brLastPointerDownAt = 0;
    this._brLastPointerDownTarget = null;
    this.boundOnBrPointerDown = this.onBrPointerDown.bind(this);
    document.addEventListener('pointerdown', this.boundOnBrPointerDown, true);

    // ── Bloomreach popup keyboard-aware content padding ───────────────────────
    // See onBrViewportResize for why this exists.
    this.boundOnBrViewportResize = this.onBrViewportResize.bind(this);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.boundOnBrViewportResize);
      this.onBrViewportResize();
    }

    this.isHeaderSearch = this.closest('header') !== null;
    this.headerSearchWrapper = this.closest('.header__search');
    this.mobileSearchTrigger =
      this.headerSearchWrapper?.nextElementSibling?.querySelector('.search-icon-mobile') ?? null;

    this.boundMenuDrawerOpen = this.handleMenuDrawerOpen.bind(this);
    document.addEventListener('menu-drawer:open', this.boundMenuDrawerOpen);

    this.boundOnMobileSearchIconClick = this.onMobileSearchIconClick.bind(this);
    this.mobileSearchTrigger?.addEventListener('click', this.boundOnMobileSearchIconClick);

    this.boundCloseMobileSearchOnScroll = this.closeMobileSearchOnScroll.bind(this);
    this.boundOnMobileSearchFocusIn = this.onMobileSearchFocusIn.bind(this);
    this.boundOnMobileSearchFocusOut = this.onMobileSearchFocusOut.bind(this);
    this._mobileSearchScrollWindowListener = false;
    this._mobileSearchScrollRaf = null;
    this._mobileSearchScrollGraceUntil = 0;

    this.boundAutoMobileSearchOnScroll = this.autoMobileSearchOnScroll.bind(this);
    this._autoMobileSearchScrollRaf = null;
    this._autoMobileSearchEnabled = false;

    if (this.isHeaderSearch) {
      this.addEventListener('focusin', this.boundOnMobileSearchFocusIn);
      this.addEventListener('focusout', this.boundOnMobileSearchFocusOut);
    }

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    this.historyContainer = this.querySelector('[data-search-history]');
    this.recentSearchesContainer = this.querySelector('[data-last-searches-container]');
    this.recentlyViewedContainer = this.querySelector('[data-recently-viewed-container]');
    this.form = this.querySelector('form');

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    this.clearSearchesButton = this.querySelector('[data-clear-searches]');
    this.recentlyViewedLoadingEl = this.querySelector('[data-recently-viewed-loading]');
    this._recentlyViewedFetchId = 0;
  }

  disconnectedCallback() {
    if (this.boundHandleClickOutside) {
      document.removeEventListener('click', this.boundHandleClickOutside);
      this.boundHandleClickOutside = null;
    }
    if (this.boundMenuDrawerOpen) {
      document.removeEventListener('menu-drawer:open', this.boundMenuDrawerOpen);
      this.boundMenuDrawerOpen = null;
    }
    if (this.boundOnMobileSearchIconClick) {
      this.mobileSearchTrigger?.removeEventListener('click', this.boundOnMobileSearchIconClick);
      this.boundOnMobileSearchIconClick = null;
    }
    if (this.boundOnMobileSearchFocusIn) {
      this.removeEventListener('focusin', this.boundOnMobileSearchFocusIn);
      this.boundOnMobileSearchFocusIn = null;
    }
    if (this.boundOnMobileSearchFocusOut) {
      this.removeEventListener('focusout', this.boundOnMobileSearchFocusOut);
      this.boundOnMobileSearchFocusOut = null;
    }
    if (this.boundOnBrPointerDown) {
      document.removeEventListener('pointerdown', this.boundOnBrPointerDown, true);
      this.boundOnBrPointerDown = null;
    }
    if (this.boundOnBrViewportResize && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.boundOnBrViewportResize);
      this.boundOnBrViewportResize = null;
    }
    this.detachAutoMobileSearch();
    this.detachMobileSearchScrollClose();
  }

  connectedCallback() {
    // Ensure the mobile header search bar is visible by default and reacts to scroll.
    // Defer a tick so layout/media queries are settled.
    requestAnimationFrame(() => this.attachAutoMobileSearch());
  }

  isMobileHeaderSearchLayout() {
    return window.matchMedia('(max-width: 767.98px)').matches;
  }

  /**
   * Mobile-only: keep the compact search bar visible near top of page.
   * - Shows on initial load
   * - Hides when user scrolls down
   * - Shows again when user scrolls back near top
   *
   * This only toggles `.header__search.active` and never touches predictive panel state.
   */
  attachAutoMobileSearch() {
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    if (!this.headerSearchWrapper) return;
    if (this._autoMobileSearchEnabled) return;
    this._autoMobileSearchEnabled = true;

    // If Liquid already set the initial state, just mirror it for a11y.
    this.setMobileSearchTriggerExpanded(this.headerSearchWrapper.classList.contains('active'));

    window.addEventListener('scroll', this.boundAutoMobileSearchOnScroll, { passive: true });
    // Run once to set correct state if page loads scrolled.
    this.autoMobileSearchOnScroll();
  }

  detachAutoMobileSearch() {
    if (!this._autoMobileSearchEnabled) return;
    this._autoMobileSearchEnabled = false;
    if (this._autoMobileSearchScrollRaf != null) {
      cancelAnimationFrame(this._autoMobileSearchScrollRaf);
      this._autoMobileSearchScrollRaf = null;
    }
    window.removeEventListener('scroll', this.boundAutoMobileSearchOnScroll, { passive: true });
  }

  autoMobileSearchOnScroll() {
    if (!this._autoMobileSearchEnabled) return;
    if (this._autoMobileSearchScrollRaf != null) return;
    this._autoMobileSearchScrollRaf = requestAnimationFrame(() => {
      this._autoMobileSearchScrollRaf = null;
      const wrapper = this.headerSearchWrapper;
      if (!wrapper) return;
      if (!this.isMobileHeaderSearchLayout()) return;

      // Don’t fight the predictive/search panel open state or active typing.
      if (document.body.classList.contains('search-open')) return;
      const active = document.activeElement;
      if (active && this.contains(active)) return;

      const y = window.scrollY;
      const showAtOrAbovePx = 48;
      const hideAtOrBelowPx = 120;

      if (y <= showAtOrAbovePx) {
        wrapper.classList.add('active');
        this.setMobileSearchTriggerExpanded(true);
      } else if (y >= hideAtOrBelowPx) {
        wrapper.classList.remove('active');
        this.setMobileSearchTriggerExpanded(false);
      }
    });
  }

  /**
   * Opens the mobile header search strip after two animation frames so the browser
   * commits the "closed" styles first; avoids skipped transitions when toggling fast.
   */
  openMobileHeaderSearchPanel() {
    const wrapper = this.headerSearchWrapper;
    if (!wrapper) return;
    this.setMobileSearchTriggerExpanded(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        wrapper.classList.add('active');
      });
    });
  }

  handleMenuDrawerOpen() {
    this.detachMobileSearchScrollClose();
    this.classList.remove('predictive-search-open');
    document.body.classList.remove('search-open');
    const wrapper = this.headerSearchWrapper;
    const showAtOrAbovePx = 48;
    const shouldKeepVisible =
      Boolean(wrapper) &&
      this._autoMobileSearchEnabled &&
      this.isMobileHeaderSearchLayout() &&
      window.scrollY <= showAtOrAbovePx;

    if (wrapper && !shouldKeepVisible) {
      wrapper.classList.remove('active');
    }
    this.setMobileSearchTriggerExpanded(Boolean(wrapper?.classList.contains('active')));
  }

  attachMobileSearchScrollClose() {
    this.detachMobileSearchScrollClose();

    const baselineThenListen = () => {
      if (this._mobileSearchScrollWindowListener) return;
      if (!this.headerSearchWrapper?.classList.contains('active')) return;
      this._mobileSearchOpenedScrollY = window.scrollY;
      /* Ignore spurious scrolls right after baseline (keyboard / URL bar / scroll-into-view on real devices). */
      this._mobileSearchScrollGraceUntil = Date.now() + 750;
      this._mobileSearchScrollWindowListener = true;
      window.addEventListener('scroll', this.boundCloseMobileSearchOnScroll, { passive: true });
    };

    const wrapper = this.headerSearchWrapper;
    if (wrapper) {
      let openTransitionEnded = false;
      const onTransitionEnd = (event) => {
        if (event.target !== wrapper) return;
        if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
        if (openTransitionEnded) return;
        openTransitionEnded = true;
        wrapper.removeEventListener('transitionend', onTransitionEnd);
        this._mobileSearchRemoveTransitionEnd = null;
        if (this._mobileSearchScrollFallbackId != null) {
          clearTimeout(this._mobileSearchScrollFallbackId);
          this._mobileSearchScrollFallbackId = null;
        }
        requestAnimationFrame(baselineThenListen);
      };
      wrapper.addEventListener('transitionend', onTransitionEnd);
      this._mobileSearchRemoveTransitionEnd = () => {
        wrapper.removeEventListener('transitionend', onTransitionEnd);
        this._mobileSearchRemoveTransitionEnd = null;
      };
    }

    /* Slightly longer than CSS (0.28s) so scroll-close still attaches if transitionend is missed. */
    this._mobileSearchScrollFallbackId = window.setTimeout(() => {
      this._mobileSearchScrollFallbackId = null;
      this._mobileSearchRemoveTransitionEnd?.();
      this._mobileSearchRemoveTransitionEnd = null;
      if (!this._mobileSearchScrollWindowListener) baselineThenListen();
    }, 340);
  }

  detachMobileSearchScrollClose() {
    this._mobileSearchScrollGraceUntil = 0;
    if (this._mobileSearchScrollFallbackId != null) {
      clearTimeout(this._mobileSearchScrollFallbackId);
      this._mobileSearchScrollFallbackId = null;
    }
    this._mobileSearchRemoveTransitionEnd?.();
    this._mobileSearchRemoveTransitionEnd = null;
    if (this._mobileSearchScrollRaf != null) {
      cancelAnimationFrame(this._mobileSearchScrollRaf);
      this._mobileSearchScrollRaf = null;
    }
    if (this._mobileSearchScrollWindowListener) {
      this._mobileSearchScrollWindowListener = false;
      window.removeEventListener('scroll', this.boundCloseMobileSearchOnScroll, { passive: true });
    }
  }

  /**
   * Real mobile browsers often change window.scrollY when the keyboard opens or the
   * viewport chrome moves — that must not count as "user scrolled away".
   */
  onMobileSearchFocusIn() {
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    if (!this.headerSearchWrapper?.classList.contains('active')) return;
    this._mobileSearchScrollGraceUntil = Date.now() + 500;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this._mobileSearchScrollWindowListener) return;
        this._mobileSearchOpenedScrollY = window.scrollY;
      });
    });
  }

  /**
   * Dismissing the on-screen keyboard (e.g. tapping the accessory bar's "Done")
   * blurs the input and shifts window.scrollY as the viewport chrome settles back —
   * same spurious-scroll problem as focus-in, just on the way out. Without this,
   * that shift gets read as "user scrolled away" and closes the whole panel.
   */
  onMobileSearchFocusOut() {
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    if (!this.headerSearchWrapper?.classList.contains('active')) return;
    this._mobileSearchScrollGraceUntil = Date.now() + 500;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this._mobileSearchScrollWindowListener) return;
        this._mobileSearchOpenedScrollY = window.scrollY;
      });
    });
  }

  onBrPointerDown(e) {
    this._brLastPointerDownAt = Date.now();
    this._brLastPointerDownTarget = e.target;
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    const popup = document.getElementById('br-autosuggest');
    if (!popup || !popup.hasAttribute('data-br-focus-trapped')) return;
    if (popup.contains(e.target) || this.input.contains(e.target)) return;
    // A real tap landed outside the popup while we were suppressing Bloomreach's
    // own blur-close (see onBrInputBlur) — its blur listener only fires once, on
    // the original input, so it will never close this popup itself. Do it for it.
    popup.remove();
  }

  /**
   * Bloomreach's own autosuggest widget closes its popup on blur unless focus
   * lands back in the input or inside the popup itself (checked after a short
   * delay). Dismissing the on-screen keyboard blurs the input with focus going
   * nowhere — whether that's via the keyboard's own "Done" control (no tap on
   * the page at all) or by tapping blank space inside the popup itself (a real
   * tap, but not on anything focusable) — both look identical to Bloomreach and
   * both get closed by its own logic. Only a tap genuinely outside the input
   * and the popup should close it, so that's the one case we let through;
   * everything else gets trapped by redirecting focus to a hidden element
   * inside the popup so Bloomreach's own check finds focus "inside" it.
   */
  onBrInputBlur() {
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    const popup = document.getElementById('br-autosuggest');
    if (!popup) return;
    const recentTap = Date.now() - this._brLastPointerDownAt < 400;
    const tappedInside =
      recentTap &&
      this._brLastPointerDownTarget &&
      (popup.contains(this._brLastPointerDownTarget) || this.input.contains(this._brLastPointerDownTarget));
    if (recentTap && !tappedInside) return;
    let trap = popup.querySelector('[data-br-focus-trap]');
    if (!trap) {
      trap = document.createElement('div');
      trap.setAttribute('tabindex', '-1');
      trap.setAttribute('data-br-focus-trap', '');
      trap.setAttribute('aria-hidden', 'true');
      trap.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
      popup.appendChild(trap);
    }
    popup.setAttribute('data-br-focus-trapped', '');
    trap.focus({ preventScroll: true });
  }

  /**
   * The popup's outer box always stays full-height (bottom:0, no max-height —
   * see assets/theme.css), matching the original full-height look regardless
   * of keyboard state. The keyboard's height is fed into --br-keyboard-inset,
   * which that same stylesheet uses as padding-bottom on the inner content
   * wrapper (.br-autosuggest__panel) — that's what actually creates scrollable
   * overflow inside the outer box, letting the user scroll up to reach rows
   * that would otherwise sit behind the keyboard with no way to get to them.
   */
  onBrViewportResize() {
    if (!window.visualViewport) return;
    const inset = Math.max(0, Math.round(window.innerHeight - window.visualViewport.height));
    document.documentElement.style.setProperty('--br-keyboard-inset', inset + 'px');
  }

  closeMobileSearchOnScroll() {
    if (this._mobileSearchScrollRaf != null) return;
    this._mobileSearchScrollRaf = requestAnimationFrame(() => {
      this._mobileSearchScrollRaf = null;

      if (!this.headerSearchWrapper?.classList.contains('active')) {
        this.detachMobileSearchScrollClose();
        return;
      }
      if (!this.isMobileHeaderSearchLayout()) return;

      const active = document.activeElement;
      if (active && this.contains(active)) return;

      if (Date.now() < this._mobileSearchScrollGraceUntil) return;

      const thresholdPx = 72;
      if (Math.abs(window.scrollY - this._mobileSearchOpenedScrollY) < thresholdPx) return;

      this.detachMobileSearchScrollClose();

      if (this.classList.contains('predictive-search-open') || document.body.classList.contains('search-open')) {
        this.toggleSearchState(false);
      } else {
        this.headerSearchWrapper.classList.remove('active');
        this.setMobileSearchTriggerExpanded(false);
      }
    });
  }

  onMobileSearchIconClick(event) {
    if (!this.isHeaderSearch || !this.isMobileHeaderSearchLayout()) return;
    event.preventDefault();
    event.stopPropagation();
    this.openMobileHeaderSearchPanel();
    this.attachMobileSearchScrollClose();
  }

  setMobileSearchTriggerExpanded(isExpanded) {
    if (!this.mobileSearchTrigger) return;
    this.mobileSearchTrigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    if (this.disableNativePopup) {
      return;
    }
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    const query = this.getQuery();
    if (query.length === 0 && !this.canShowEmptyQueryPanel()) {
      return;
    }

    if (this.isHeaderSearch) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
      if (this.isMobileHeaderSearchLayout()) {
        this.openMobileHeaderSearchPanel();
      }
    }

    this.searchContent?.classList.remove('hidden');
    this.classList.add('predictive-search-open');
    if (query.length === 0) {
      this.renderHistory();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    if (this.disableNativePopup) {
      return;
    }
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    return this.buildPredictiveUrl(this.getQuery());
  }

  onChange() {
    if (this.disableNativePopup) {
      return;
    }
    if (this.getQuery().length === 0) {
      this.clearHistoryView();
      this.removeAttribute('results');
      if (!this.canShowEmptyQueryPanel()) {
        this.toggleSearchState(false);
        return;
      }
      this.renderHistory();
      return;
    }
    this.clearHistoryView();
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  buildPredictiveUrl(query) {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let searchTerm = query;
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      searchTerm = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(searchTerm)}`;
    }
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,tag,vendor');
    url.searchParams.set('resources[options][unavailable_products]', 'show');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  handleSubmit() {
    const query = this.getQuery();
    if (query.length === 0) return;
    this.saveSearchTerm(query);
  }

  saveSearchTerm(term) {
    if (!FoxTheme.config?.hasLocalStorage) return;
    const key = 'hypertheme:last-searches';
    let items = [];
    try {
      items = JSON.parse(window.localStorage.getItem(key) || '[]');
      if (!Array.isArray(items)) items = [];
    } catch (e) {
      items = [];
    }

    const normalized = term.trim();
    if (!normalized) return;

    items = items.filter((item) => item !== normalized);
    items.unshift(normalized);
    window.localStorage.setItem(key, JSON.stringify(items.slice(0, 5)));
  }

  getRecentSearchTerms() {
    if (!FoxTheme.config?.hasLocalStorage) return [];
    const key = 'hypertheme:last-searches';
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return [];
      const items = JSON.parse(raw);
      return Array.isArray(items) ? items : [];
    } catch (e) {
      return [];
    }
  }

  getRecentlyViewedIds() {
    if (!FoxTheme.config?.hasLocalStorage) return [];
    try {
      const items = JSON.parse(window.localStorage.getItem('hypertheme:recently-viewed') || '[]');
      return Array.isArray(items) ? items.filter((id) => id != null && id !== '') : [];
    } catch (e) {
      return [];
    }
  }

  canShowEmptyQueryPanel() {
    if (!this.searchRecommendationEmpty) return true;
    return this.getRecentSearchTerms().length > 0 || this.getRecentlyViewedIds().length > 0;
  }

  showRecentlyViewedLoading() {
    this.recentlyViewedLoadingEl?.classList.remove('hidden');
  }

  hideRecentlyViewedLoading() {
    this.recentlyViewedLoadingEl?.classList.add('hidden');
  }

  maybeCloseHistoryIfEmpty() {
    if (!this.searchRecommendationEmpty) return;
    const hasTerms = this.getRecentSearchTerms().length > 0;
    const loadingOn =
      this.recentlyViewedLoadingEl && !this.recentlyViewedLoadingEl.classList.contains('hidden');
    const rvHasContent =
      this.recentlyViewedContainer &&
      this.recentlyViewedContainer.children.length > 0 &&
      !this.recentlyViewedContainer.classList.contains('hidden');
    if (!hasTerms && !rvHasContent && !loadingOn) {
      this.toggleSearchState(false);
    }
  }

  renderRecentSearches() {
    if (!this.recentSearchesContainer || !FoxTheme.config?.hasLocalStorage) return;
    const listElement =
      this.recentSearchesContainer.querySelector('[data-last-searches-list]') ||
      this.recentSearchesContainer.querySelector('ul');
    if (!listElement) return;

    const terms = this.getRecentSearchTerms();
    listElement.innerHTML = '';

    if (!terms.length) {
      this.recentSearchesContainer.classList.add('hidden');
      this.maybeCloseHistoryIfEmpty();
      return;
    }

    this.recentSearchesContainer.classList.remove('hidden');

    const handleItemClick = (term) => {
      this.input.value = term;
      this.input.focus();
      this.onChange();
    };

    const handleItemRemove = (term, li) => {
      const key = 'hypertheme:last-searches';
      let items = this.getRecentSearchTerms().filter((item) => item !== term);
      window.localStorage.setItem(key, JSON.stringify(items.slice(0, 5)));
      li.remove();

      if (!listElement.children.length) {
        this.recentSearchesContainer.classList.add('hidden');
        this.maybeCloseHistoryIfEmpty();
      }
    };

    terms.slice(0, 5).forEach((term) => {
      const li = document.createElement('li');
      li.className = 'flex items-center justify-between gap-2';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn--plain';
      button.textContent = term;
      button.addEventListener('click', () => handleItemClick(term));

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'btn--plain';
      removeBtn.setAttribute('aria-label', 'Remove search');
      removeBtn.innerHTML =
        '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M5.62124 4.20328L8.41584 6.99788L7.00162 8.41209L4.20703 5.6175L1.41244 8.41209L-0.00177379 6.99788L2.79282 4.20328L0.00375072 1.41422L1.41796 4.14095e-06L4.20703 2.78907L6.9961 4.37605e-06L8.41031 1.41422L5.62124 4.20328Z" fill="black"/>' +
        '</svg>';
      removeBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        handleItemRemove(term, li);
      });

      li.appendChild(button);
      li.appendChild(removeBtn);
      listElement.appendChild(li);
    });

    if (this.clearSearchesButton) {
      this.clearSearchesButton.onclick = () => {
        const key = 'hypertheme:last-searches';
        window.localStorage.removeItem(key);
        listElement.innerHTML = '';
        this.recentSearchesContainer.classList.add('hidden');
        this.maybeCloseHistoryIfEmpty();
      };
    }
  }

  renderRecentlyViewedProducts() {
    if (!this.recentlyViewedContainer || !FoxTheme.config?.hasLocalStorage) return;

    const items = this.getRecentlyViewedIds();

    if (!items.length) {
      this.hideRecentlyViewedLoading();
      this.recentlyViewedContainer.classList.add('hidden');
      this.recentlyViewedContainer.innerHTML = '';
      this.maybeCloseHistoryIfEmpty();
      return;
    }

    const ids = items.slice(0, 3);
    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = this.buildPredictiveUrl(query).toString();

    const fetchId = ++this._recentlyViewedFetchId;
    this.showRecentlyViewedLoading();
    this.recentlyViewedContainer.classList.add('hidden');
    this.recentlyViewedContainer.innerHTML = '';

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        if (fetchId !== this._recentlyViewedFetchId) return;
        this.hideRecentlyViewedLoading();
        const parser = new DOMParser();
        const doc = parser.parseFromString(responseText, 'text/html');
        const sectionId = FoxTheme.utils.getSectionId(this);
        const resultsRoot = doc.getElementById(`PredictiveSearchResults-${sectionId}`);
        const productsList = resultsRoot?.querySelector('#ps-list-products');

        if (!productsList || !productsList.children.length) {
          this.recentlyViewedContainer.classList.add('hidden');
          this.recentlyViewedContainer.innerHTML = '';
          this.maybeCloseHistoryIfEmpty();
          return;
        }

        const wrapper = document.createElement('div');
        wrapper.className =
          'search__products-recommendation search__box-products flex flex-col flex-grow gap-5 lg:gap-6';

        const heading = document.createElement('h4');
        heading.className = 'h4';
        heading.textContent = 'Recently viewed products';
        wrapper.appendChild(heading);

        const swipeWrapper = document.createElement('div');
        swipeWrapper.className = 'swipe-mobile swipe-mobile--2-cols';

        const inner = document.createElement('div');
        inner.className = productsList.className;
        inner.innerHTML = productsList.innerHTML;

        swipeWrapper.appendChild(inner);
        wrapper.appendChild(swipeWrapper);

        this.recentlyViewedContainer.innerHTML = '';
        this.recentlyViewedContainer.appendChild(wrapper);
        this.recentlyViewedContainer.classList.remove('hidden');
      })
      .catch(() => {
        if (fetchId !== this._recentlyViewedFetchId) return;
        this.hideRecentlyViewedLoading();
        this.recentlyViewedContainer.classList.add('hidden');
        this.recentlyViewedContainer.innerHTML = '';
        this.maybeCloseHistoryIfEmpty();
      });
  }

  renderHistory() {
    if (!this.historyContainer) return;
    const hasTerms = this.getRecentSearchTerms().length > 0;
    const hasRvIds = this.getRecentlyViewedIds().length > 0;
    if (!hasTerms && !hasRvIds) {
      this.historyContainer.classList.add('hidden');
      this.hideRecentlyViewedLoading();
      return;
    }
    this.historyContainer.classList.remove('hidden');
    this.renderRecentlyViewedProducts();
    this.renderRecentSearches();
  }

  clearHistoryView() {
    this._recentlyViewedFetchId++;
    this.hideRecentlyViewedLoading();
    if (!this.historyContainer) return;
    this.historyContainer.classList.add('hidden');

    if (this.recentSearchesContainer) {
      const listElement =
        this.recentSearchesContainer.querySelector('[data-last-searches-list]') ||
        this.recentSearchesContainer.querySelector('ul');
      if (listElement) listElement.innerHTML = '';
    }

    if (this.recentlyViewedContainer) {
      this.recentlyViewedContainer.innerHTML = '';
      this.recentlyViewedContainer.classList.add('hidden');
    }
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const clickedSearchClose = target.closest('.header__search-close');
    const clickedMenuDrawerOverlay =
      target.classList?.contains('fixed-overlay') && target.getAttribute?.('aria-controls') === 'MenuDrawer';
    const isHeaderOverlayClick =
      target.classList.contains('fixed-overlay') && target.closest('.header-section') && !clickedMenuDrawerOverlay;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) && (Boolean(clickedSearchClose) || isHeaderOverlayClick)
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
      if (!isOpen) {
        this.detachMobileSearchScrollClose();
        const wrapper = this.headerSearchWrapper;
        const showAtOrAbovePx = 48;
        const shouldKeepVisible =
          Boolean(wrapper) &&
          this._autoMobileSearchEnabled &&
          this.isMobileHeaderSearchLayout() &&
          window.scrollY <= showAtOrAbovePx;

        if (wrapper && !shouldKeepVisible) {
          wrapper.classList.remove('active');
        } else if (wrapper && shouldKeepVisible) {
          wrapper.classList.add('active');
        }
        this.setMobileSearchTriggerExpanded(Boolean(wrapper?.classList.contains('active')));
        this.input?.blur();
      }
    }
    if (!isOpen) {
      this.searchContent?.classList.add('hidden');
      this.clearHistoryView();
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
